import type {
  HandOffConversation,
  HandoffMessage,
  HandoffResolvedPeriod,
  MailFailure,
  MailFailureFilter,
} from 'types/handoff';

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(message || `Request failed with ${res.status}`);
  }

  return res.json() as Promise<T>;
}

const withCreds: RequestInit = { credentials: 'include' };

export async function getHandoffConversations(): Promise<HandOffConversation[]> {
  const res = await fetch('/api/handoff', withCreds);
  return parseJson<HandOffConversation[]>(res);
}

export async function getHandoffMessages(conversationId: string): Promise<HandoffMessage[]> {
  const res = await fetch(`/api/handoff/${conversationId}/messages`, withCreds);
  return parseJson<HandoffMessage[]>(res);
}

export async function getResolvedHandoffConversations(
  period: HandoffResolvedPeriod,
): Promise<HandOffConversation[]> {
  const res = await fetch(`/api/handoff/resolved?period=${period}`, withCreds);
  return parseJson<HandOffConversation[]>(res);
}

export async function getMailFailures(filter: MailFailureFilter): Promise<MailFailure[]> {
  const res = await fetch(`/api/handoff/mail-failures?filter=${filter}`, withCreds);
  return parseJson<MailFailure[]>(res);
}

export async function takeHandoff(conversationId: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/handoff/${conversationId}/take`, {
    method: 'POST',
    credentials: 'include',
  });
  return parseJson(res);
}

export async function assignHandoff(
  conversationId: string,
  agentId: string,
): Promise<{ success: boolean }> {
  const res = await fetch(`/api/handoff/${conversationId}/assign`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ agentId }),
  });
  return parseJson(res);
}

export async function resolveHandoff(conversationId: string): Promise<{ success: boolean }> {
  const res = await fetch(`/api/handoff/${conversationId}/resolve`, {
    method: 'POST',
    credentials: 'include',
  });
  return parseJson(res);
}

export type HandoffSettings = { liveAgentsEnabled: boolean };

export async function getHandoffSettings(): Promise<HandoffSettings> {
  const res = await fetch('/api/handoff/settings', withCreds);
  return parseJson<HandoffSettings>(res);
}

export async function setLiveAgentsEnabled(liveAgentsEnabled: boolean): Promise<HandoffSettings> {
  const res = await fetch('/api/handoff/settings', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ liveAgentsEnabled }),
  });
  return parseJson<HandoffSettings>(res);
}

export async function sendAgentMessage(params: {
  conversationId: string;
  content: string;
}): Promise<HandoffMessage> {
  const res = await fetch(`/api/handoff/${params.conversationId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      content: params.content,
    }),
  });

  return parseJson<HandoffMessage>(res);
}
